import { dashMetaFetch } from '../../shared/api/dash-meta-fetch';
import type {
  GlobalAiConversationListItem,
  GlobalAiConversationPayloadV1,
} from './global-ai-conversation-persistence';

const CONVERSATIONS_PATH = '/api/build-a-dash/global-assistant/conversations';

/** HTTP-status-carrying error so callers can branch (e.g. POST 409 → PUT). */
export class GlobalAiConversationApiError extends Error {
  constructor(
    message: string,
    public readonly status: number
  ) {
    super(message);
    this.name = 'GlobalAiConversationApiError';
  }
}

type FetchArgs = {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
  body?: unknown;
  signal?: AbortSignal;
};

async function conversationFetch(args: FetchArgs): Promise<Response> {
  const response = await dashMetaFetch(args.path, {
    method: args.method,
    ...(args.body !== undefined ? { body: JSON.stringify(args.body) } : {}),
    ...(args.signal ? { signal: args.signal } : {}),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new GlobalAiConversationApiError(
      text || `HTTP ${response.status}`,
      response.status
    );
  }
  return response;
}

export async function listGlobalAiConversations(
  signal?: AbortSignal
): Promise<GlobalAiConversationListItem[]> {
  const response = await conversationFetch({
    method: 'GET',
    path: CONVERSATIONS_PATH,
    signal,
  });
  return response.json() as Promise<GlobalAiConversationListItem[]>;
}

export async function getGlobalAiConversation(
  id: string,
  signal?: AbortSignal
): Promise<{
  id: string;
  title: string;
  schemaVersion: number;
  payload: unknown;
}> {
  const response = await conversationFetch({
    method: 'GET',
    path: `${CONVERSATIONS_PATH}/${id}`,
    signal,
  });
  return response.json() as Promise<{
    id: string;
    title: string;
    schemaVersion: number;
    payload: unknown;
  }>;
}

export async function createGlobalAiConversation(body: {
  id: string;
  title: string;
  schemaVersion: number;
  payload: GlobalAiConversationPayloadV1;
}): Promise<{ id: string }> {
  const response = await conversationFetch({
    method: 'POST',
    path: CONVERSATIONS_PATH,
    body,
  });
  return response.json() as Promise<{ id: string }>;
}

export async function updateGlobalAiConversation(
  id: string,
  body: {
    title?: string;
    schemaVersion?: number;
    payload?: GlobalAiConversationPayloadV1;
  }
): Promise<void> {
  await conversationFetch({
    method: 'PUT',
    path: `${CONVERSATIONS_PATH}/${id}`,
    body,
  });
}

export async function deleteGlobalAiConversation(id: string): Promise<void> {
  await conversationFetch({
    method: 'DELETE',
    path: `${CONVERSATIONS_PATH}/${id}`,
  });
}

export async function postGlobalAssistantGenerateTitle(body: {
  prompt: string;
  assistantText?: string;
}): Promise<{ title: string }> {
  const response = await conversationFetch({
    method: 'POST',
    path: '/api/build-a-dash/ai-gen/global-assistant/generate-title',
    body,
  });
  return response.json() as Promise<{ title: string }>;
}
